/**
 * Sanitize OpenPanel track envelope before send (filter-hook).
 *
 * Three responsibilities (OpenPanel @openpanel/web@1.3.0 не имеет нативного API для них):
 *   1. Whitelist `properties.__query.*`. Удаляем ключи кроме utm_*, fbclid, rb_clickid —
 *      на случай если SDK когда-нибудь начнёт заполнять их клиентом.
 *   2. Strip non-whitelisted query string из URL-полей. SDK шлёт `__path = window.location.href`,
 *      OpenPanel server парсит query → создаёт `__query.accessToken/email/...` → утечка JWT/PII.
 *      Режем query на клиенте до отправки.
 *   3. UUID normalization. `/auth/8bcff9c6-.../page` → `/auth/[id]/page` — page-аналитика взрывается
 *      из-за path-explosion (P1-5) если UUID попадают в top-level `path` колонку ClickHouse.
 *
 * Принимает SDK envelope `{ type, payload: { properties } }` от filter-hook. No-op для не-track.
 * Mutates payload in-place.
 */

import { currentPageSecrets, redactPageSecrets, redactSecretPaths } from '../page-secret-paths';

const QUERY_WHITELIST: ReadonlySet<string> = new Set(['fbclid', 'rb_clickid']);

const QUERY_WHITELIST_PREFIXES: readonly string[] = ['utm_'];

const QUERY_PROPERTY_PREFIX: string = '__query.';

const PATH_KEYS: readonly string[] = ['__path', 'path', 'url', '__referrer', 'referrer'];

// Путь страницы и запроса в error_shown: секрет пути режется, query там уже нет, а UUID дашборды читают как есть.
const ERROR_PATH_KEYS: readonly string[] = ['page', 'endpoint'];

const UUID_REGEX: RegExp = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

function isWhitelistedQueryKey(queryKey: string): boolean {
  if (QUERY_WHITELIST.has(queryKey)) {
    return true;
  }

  return QUERY_WHITELIST_PREFIXES.some(prefix => queryKey.startsWith(prefix));
}

export function normalizeUuidsInUrl(rawValue: string): string {
  return rawValue.replace(UUID_REGEX, '[id]');
}

function stripNonWhitelistedQuery(rawValue: string): string {
  const queryStartIndex: number = rawValue.indexOf('?');
  if (queryStartIndex === -1) {
    return rawValue;
  }

  const hashStartIndex: number = rawValue.indexOf('#', queryStartIndex);
  const hasHash: boolean = hashStartIndex !== -1;
  const queryEndIndex: number = hasHash ? hashStartIndex : rawValue.length;

  const head: string = rawValue.slice(0, queryStartIndex);
  const queryString: string = rawValue.slice(queryStartIndex + 1, queryEndIndex);
  const tail: string = hasHash ? rawValue.slice(hashStartIndex) : '';

  const params = new URLSearchParams(queryString);
  const filtered = new URLSearchParams();
  for (const [key, value] of params) {
    if (isWhitelistedQueryKey(key)) {
      filtered.append(key, value);
    }
  }

  const filteredQuery: string = filtered.toString();
  return filteredQuery.length > 0 ? `${head}?${filteredQuery}${tail}` : `${head}${tail}`;
}

interface TrackEnvelope {
  type: 'track';
  payload: { properties?: Record<string, unknown> };
}

function isTrackEnvelope(value: unknown): value is TrackEnvelope {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const candidate: { type?: unknown; payload?: unknown } = value as Record<string, unknown>;
  if (candidate.type !== 'track') {
    return false;
  }

  return typeof candidate.payload === 'object' && candidate.payload !== null;
}

export function sanitizeTrackPayload(envelope: unknown): void {
  if (!isTrackEnvelope(envelope)) {
    return;
  }

  const properties: Record<string, unknown> | undefined = envelope.payload.properties;
  if (!properties) {
    return;
  }

  for (const propertyKey of Object.keys(properties)) {
    if (!propertyKey.startsWith(QUERY_PROPERTY_PREFIX)) {
      continue;
    }

    const queryKey: string = propertyKey.slice(QUERY_PROPERTY_PREFIX.length);
    if (!isWhitelistedQueryKey(queryKey)) {
      delete properties[propertyKey];
    }
  }

  // Секрет текущей страницы маскируется и там, где маска пути его не узнаёт: /client;x=%2F/<токен>, referrer.
  const pageSecrets: string[] = currentPageSecrets();

  for (const pathKey of PATH_KEYS) {
    const rawValue: unknown = properties[pathKey];
    if (typeof rawValue === 'string' && rawValue.length > 0) {
      const stripped: string = stripNonWhitelistedQuery(redactPageSecrets(redactSecretPaths(rawValue), pageSecrets));
      properties[pathKey] = normalizeUuidsInUrl(stripped);
    }
  }

  for (const pathKey of ERROR_PATH_KEYS) {
    const rawValue: unknown = properties[pathKey];
    if (typeof rawValue === 'string') {
      properties[pathKey] = redactPageSecrets(redactSecretPaths(rawValue), pageSecrets);
    }
  }
}
